import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TickerWithName from "../components/TickerWithName";
import api from "../utils/api";
import { QuickTradeEquityButton, QuickTradeOptionButton } from "../components/QuickTradeButtons";


// helpers
const money2 = (v) => (v == null || v === '' ? '0.00' : (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : String(v)));
const pctChange = (a, b) => {
    const x = Number(a), y = Number(b);
    if (!Number.isFinite(x) || !Number.isFinite(y) || x === 0) return null;
    return ((y - x) / x) * 100;
};
const EstExitLine = ({ entry, exit }) => {
    const pct = pctChange(entry, exit);
    const good = pct !== null && pct >= 0;
    return (
        <div>
            Estimated Exit: ${money2(exit)}{" "}
            {pct !== null && (
                <span className={`ml-1 px-2 py-0.5 rounded text-xs font-semibold ${good ? "bg-green-600" : "bg-red-600"}`}>
                    {good ? "+" : ""}{pct.toFixed(2)}%
                </span>
            )}
        </div>
    );
};
const LiveDot = ({ show }) => show ? <span title="live price" className="ml-2 w-2 h-2 rounded-full bg-green-400 inline-block" /> : null;

const SearchPage = () => {
    const [ticker, setTicker] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleSearch = () => {
        if (!ticker.trim()) return;
        api
            .get(`/api/search/${ticker.trim().toUpperCase()}`, { timeout: 20000 })
            .then(res => {
                setResult(res.data);
                setError('');
            })
            .catch(() => {
                setResult(null);
                setError('Ticker not found or not enough data.');
            });
    };

    return (
        <div className="p-4 sm:p-5">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-blue-400 drop-shadow">🔍 Search a Stock</h2>

            <div className="flex flex-col sm:flex-row mb-4">
                <input
                    type="text"
                    value={ticker}
                    onChange={e => setTicker(e.target.value)}
                    placeholder="Enter Ticker Symbol"
                    className="px-4 py-2 rounded-t sm:rounded-l sm:rounded-tr-none bg-gray-800 border border-gray-600 w-full sm:w-64"
                />
                <button
                    onClick={handleSearch}
                    className="px-4 py-2 rounded-b sm:rounded-r sm:rounded-bl-none bg-blue-700 hover:bg-blue-600"
                >
                    Search
                </button>
            </div>
            <div className="mb-6">
                <button
                    onClick={() => navigate("/options-search")}
                    className="px-4 py-2 rounded bg-purple-700 hover:bg-purple-600 text-white shadow-md"
                >
                    ➡️ Go to Options Chain Picker
                </button>
            </div>

            {error && <div className="text-red-400 mb-4">{error}</div>}

            {result && (
                <div className="bg-gray-700 p-4 sm:p-5 rounded shadow">
                    <div
                        className="text-xl sm:text-2xl font-bold mb-2 cursor-pointer hover:text-blue-400 drop-shadow-sm"
                        onClick={() => navigate(`/chart/${result.ticker}`)}
                    >
                        <TickerWithName symbol={result.ticker} />
                    </div>

                    <div className="text-sm text-gray-300 mb-2">Score: {result.score}</div>

                    <div className="flex items-center">
                        Entry Price: ${money2(result.entryPrice)} <LiveDot show={result.priceSource === 'live'} />
                    </div>
                    <EstExitLine entry={result.entryPrice} exit={result.estimatedExit} />

                    <div className="text-sm text-gray-400 mb-4">Reason: {result.reason}</div>
                    <div className="mt-2 flex gap-2">
                        <QuickTradeEquityButton ticker={result.ticker} />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold mb-2 text-blue-300">Options Trades</h3>
                    {(result.optionsTrades && result.optionsTrades.length > 0) ? (
                        <div className="space-y-2">
                            {result.optionsTrades.map((opt, idx) => (
                                <div key={idx} className="bg-gray-800 p-3 sm:p-4 rounded space-y-1">
                                    <div className="flex items-center">
                                        <span>Strategy: {opt.strategy}</span>
                                        <span className={`ml-2 px-2 py-0.5 text-xs font-bold rounded ${(opt.strategy || '').toUpperCase() === 'CALL' ? 'bg-green-600' : 'bg-red-600'}`}>
                                            {(opt.strategy || '').toUpperCase()}
                                        </span>
                                    </div>
                                    <div>Strike: {opt.strike}</div>
                                    <div>Expiration: {opt.expiration}</div>
                                    <div>Entry Price: ${money2(opt.entryPrice)}</div>
                                    <div>Estimated Exit: ${money2(opt.estimatedExit)}</div>
                                    {'confidence' in opt && <div>Confidence: {opt.confidence}%</div>}
                                    <div className="mt-2 flex gap-2">
                                        <QuickTradeOptionButton
                                            ticker={result.ticker}
                                            strategy={opt.strategy}
                                            strike={opt.strike}
                                            expiration={opt.expiration}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div>No options data available.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchPage;
